import { useCallback, useEffect, useState } from 'react';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import { useSendContactMutation } from 'views/utils/graphql/graphql';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ErrorWord, getMaxLengthErrorMessage } from 'views/templates/accountOpen/common';
import { CommonData } from 'views/templates/module/common';
import { formErrorScroll } from 'views/mutual/formErrorScroll';

export enum ContactPageStep {
  Detail,
  Confirm,
  Complete,
}


export type ContactType = {
  contactDetails: string;
};

export const useContact = () => {
  const [contactPageStep, setContactPageStep] = useState<ContactPageStep>(ContactPageStep.Detail);
  const [_, sendContact] = useSendContactMutation();
  const { showError, messageClose } = useGlobalToastHandler();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validationSchema = Yup.object().shape({
    contactDetails: Yup.string().required(ErrorWord.INPUT).max(32768, getMaxLengthErrorMessage(32768)),
  });

  const formik = useFormik<ContactType>({
    initialValues: {
      contactDetails: '',
    },
    onSubmit: (values, { setSubmitting }) => {
      onSubmit(values);
      setSubmitting(false);
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    validate: () => {
      messageClose();
    },
  });

  const contactProps = {
    contactDetailsProps: {
      value: formik.values.contactDetails,
      onChange: (value: string) => formik.setFieldValue('contactDetails', value),
      error: formik.errors.contactDetails,
    },
  };

  const onSubmit = useOnceSubmit(
    {
      onSubmit: async (input) => {        
        switch (contactPageStep) {
          case ContactPageStep.Detail:
            setContactPageStep(ContactPageStep.Confirm);
            break;
          case ContactPageStep.Confirm:
            const res = await sendContact({
              input: {
                Details : formik.values.contactDetails
              },
            });
            if (res?.data) {
              if (res.data.SendContact?.Code === 0) {
                setContactPageStep(ContactPageStep.Complete);
              } else {
                showError(res.data.SendContact?.Message);
              }
            }
            break;
          case ContactPageStep.Complete:
            break;
          default:
            showError('不明なステップです');
        }
      },
    },
    [contactPageStep, setContactPageStep, showError, messageClose],
  );

  const backPage = useCallback(() => {
    console.log(`contactPageStep : ${contactPageStep}`)
    if (contactPageStep === ContactPageStep.Confirm) {      
      setContactPageStep(ContactPageStep.Detail);
    }
  }, [contactPageStep]);

  useEffect(() => {
    formErrorScroll();
  }, [formik.isSubmitting]);

  return {
    formik,
    contactPageStep,
    backPage,
    contactProps,
  };
};
